/**
 * 화자 구분 — 외부 API 탭의 보기 하나 (2026-10-22 · 시안 SD1 · SD3 와 디자이너 평 «묶음 2»).
 *
 * 서버: 범주 `diarize` · 어댑터 `diarize_pyannote_http` (`apps/chat-server/src/lib/external-api/diarize-adapters.ts`).
 * 표는 STT 와 같은 꼴이고, 평에서 고칠 것으로 받은 셋을 담았다.
 *
 * ## 상태는 두 축이다
 *
 *   · 「상태」 칸 = 켜짐 / 꺼짐 — 관리자가 정한 것.
 *   · 「마지막 시험」 칸 = 마지막 연결 결과(연결 정상 · 키 거절 · 닿지 못함 · 엔진 오류) — 서버가 잰 것.
 *   한 칸에 섞으면 「켜 두었는데 안 되는 것」과 「꺼 둔 것」이 같은 빨강으로 보인다.
 *
 * ## 실패는 그 줄에서 해결 문장까지
 *
 * 「키 거절 — 키를 다시 넣으세요」를 굵은 위험색으로 적고, 그 문장을 누르면 그 엔진의 «고치기»가 열린다.
 * 켜 둔 엔진이 실패하는 동안 무엇이 달라지는지(새 녹음은 화자 없이 보인다)를 같은 칸에 적는다.
 *
 * ## 평문 http 는 그 행에 표지로
 *
 * 표 아래 늘 켜진 띠로 두면 엔진이 둘 이상일 때 어느 줄 얘기인지 모르고, 늘 켜진 노랑은 곧 안 읽힌다.
 *
 * ## 서버가 화자 구분을 모르면
 *
 * 탭 쪽(`ExternalApiSection`)이 어댑터가 있을 때만 이 보기를 연다. 그래도 어댑터 없이 줄이 남아 있는 판
 * (되돌린 서버 등)에서는 줄을 보여 주되 고치기를 열지 않는다 — 눌러도 서버가 거절하는 단추를 만들지 않는다.
 */
import { Globe, Info } from '../../../icons';
import type { ExternalApiAdapter, ExternalApiProvider } from '../../../api/externalApis';
import { ConsoleListTable } from '../../../components/console-list/ConsoleListTable';
import { DIARIZE_PATH, checkDiarizeEndpoint, formatDuration, readDiarizeConfig } from './diarizeEndpoint';
import './Diarize.css';

/** 마지막 연결 결과의 이름. */
const RESULT_LABEL: Record<string, string> = {
  ok: '연결 정상',
  auth_failed: '키 거절',
  unreachable: '닿지 못함',
  provider_error: '엔진 오류',
  shared_quota_exhausted: '한도 소진',
};

/** 실패했을 때 «무엇을 하면 되는지». */
const RESULT_FIX: Record<string, string> = {
  auth_failed: '키를 다시 넣으세요',
  unreachable: '끝점 주소와 포트를 다시 보세요',
  provider_error: '서비스 상태를 보고 다시 시험하세요',
  shared_quota_exhausted: '잠시 뒤 다시 시험하세요',
};

/** 마지막 시험이 실패였나 — 아직 안 했으면 실패가 아니다. */
export function diarizeFailed(p: ExternalApiProvider): boolean {
  return p.last_probe_status !== null && p.last_probe_status !== 'ok';
}

/** 실패 줄에 적는 한 문장 — 「키 거절 — 키를 다시 넣으세요」. */
export function diarizeFixText(status: string): string {
  const label = RESULT_LABEL[status] ?? status;
  return `${label} — ${RESULT_FIX[status] ?? '고치기에서 다시 보세요'}`;
}

function fmtDate(iso: string | null): string {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('ko-KR', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
}

export interface DiarizePanelProps {
  /** 검색으로 거른 줄. */
  providers: ExternalApiProvider[];
  /** 거르기 전 줄 수 — 「엔진이 없다」와 「찾는 엔진이 없다」를 가른다. */
  total: number;
  adapters: ExternalApiAdapter[];
  loading: boolean;
  busyKey: string | null;
  onTest: (p: ExternalApiProvider) => void;
  onOpen: (p: ExternalApiProvider) => void;
}

export function DiarizePanel({ providers, total, adapters, loading, busyKey, onTest, onOpen }: DiarizePanelProps) {
  /** 서버가 부를 줄 아는 화자 구분 엔진이 있나 — 깃발이 아니라 서버가 준 어댑터 목록으로 정한다. */
  const serverReady = adapters.some((a) => a.category === 'diarize');

  return (
    <div className="xa-dz">
      {!loading && !serverReady && (
        <div className="xa-band xa-band--info" role="status">
          <Globe size={18} aria-hidden />
          <span className="xa-band__t">
            <b>서버가 아직 화자 구분을 부를 줄 모릅니다</b>
            <span>서버에 화자 구분 어댑터가 들어간 뒤에 고칠 수 있습니다 — 그때 이 화면이 저절로 열립니다.</span>
          </span>
        </div>
      )}

      {!loading && serverReady && total === 0 && (
        <div className="xa-band xa-band--info" role="status">
          <Info size={18} aria-hidden />
          <span className="xa-band__t">
            <b>등록된 화자 구분 엔진이 없습니다.</b>
            <span>없으면 대화내역은 지금처럼 화자 없이 보입니다(받아쓰기는 그대로). 「엔진 등록」으로 시작합니다.</span>
          </span>
        </div>
      )}

      {(loading || total > 0) && (
        <ConsoleListTable
          rows={providers}
          rowKey={(p) => p.provider_key}
          onRowClick={serverReady ? onOpen : undefined}
          empty={loading ? '불러오는 중…' : '찾는 엔진이 없습니다'}
          columns={[
            {
              key: 'engine',
              header: '엔진',
              render: (p) => (
                <span className="xa-dz-cell">
                  <b>{p.label}</b>
                  <span className="xa-dz-sub">{p.provider_key}{p.adapter_available ? '' : ' · 부를 수 없음'}</span>
                </span>
              ),
            },
            {
              key: 'endpoint',
              header: '끝점',
              render: (p) => {
                const cfg = readDiarizeConfig(p);
                if (cfg.endpoint === '') return <span className="xa-dim">—</span>;
                const ep = checkDiarizeEndpoint(cfg.endpoint);
                return (
                  <span className="xa-dz-cell">
                    <span className="xa-dz-ep">
                      {/* 한 줄 말줄임 — 전체 주소는 title 로. 주소 가운데서 줄이 갈리면 옮겨 적다 틀린다. */}
                      <span className="xa-dz-ep__t" title={cfg.endpoint}>{cfg.endpoint}</span>
                      {ep.ok && ep.plaintext && (
                        <span
                          className="xa-badge xa-badge--warn xa-dz-tag"
                          title="평문 http — 회의 녹음이 암호화 없이 지나갑니다"
                        >
                          평문
                        </span>
                      )}
                    </span>
                    {/* 경로를 안 적었으면 서버가 붙이는 경로를 보여 준다 — 무엇을 부르는지 감추지 않는다. */}
                    {ep.ok && ep.hostOnly && <span className="xa-dz-sub">{DIARIZE_PATH}</span>}
                  </span>
                );
              },
            },
            {
              key: 'key',
              header: '키',
              render: (p) => {
                if (p.key_state === 'unreadable') return <span className="xa-badge xa-badge--bad">키를 못 읽음</span>;
                if (p.key_state === 'stored') {
                  return (
                    <span className="xa-key">
                      <b>{p.key_hint ?? '••••'}</b>
                      <span>{p.key_hint ? '저장됨' : '저장됨 · 끝 넉 자 모름'}</span>
                    </span>
                  );
                }
                if (p.env_key_fallback) return <span className="xa-badge xa-badge--info">개발키로 돎</span>;
                return <span className="xa-badge xa-badge--muted">키 없음</span>;
              },
            },
            {
              key: 'timeout',
              header: '타임아웃',
              render: (p) => {
                const sec = readDiarizeConfig(p).timeout_sec;
                return <span className="xa-dz-num" title={`${sec}초`}>{formatDuration(sec)}</span>;
              },
            },
            {
              key: 'state',
              header: '상태',
              render: (p) => (
                <span className="xa-dz-cell">
                  {p.enabled
                    ? <span className="xa-badge xa-badge--ok">켜짐</span>
                    : <span className="xa-badge xa-badge--muted">꺼짐</span>}
                  {!p.enabled && <span className="xa-dz-sub">새 녹음은 화자 없이 보입니다</span>}
                </span>
              ),
            },
            {
              key: 'probe',
              header: '마지막 시험',
              render: (p) => {
                const s = p.last_probe_status;
                if (s === null) return <span className="xa-dim">아직 안 함</span>;
                if (s === 'ok') {
                  return (
                    <span className="xa-dz-cell">
                      <span className="xa-badge xa-badge--ok">{RESULT_LABEL.ok}</span>
                      <span className="xa-dz-sub">{fmtDate(p.last_probe_at)}</span>
                    </span>
                  );
                }
                const fix = diarizeFixText(s);
                return (
                  <span className="xa-dz-result">
                    <span className="xa-dz-sub">{fmtDate(p.last_probe_at)}</span>
                    {serverReady
                      ? (
                        <button
                          type="button"
                          className="xa-dz-fix"
                          onClick={(e) => { e.stopPropagation(); onOpen(p); }}
                        >
                          {fix}
                        </button>
                      )
                      : <b className="xa-dz-fix">{fix}</b>}
                    {p.enabled && <span className="xa-dz-note">실패하는 동안 새 녹음은 화자 없이 보입니다</span>}
                  </span>
                );
              },
            },
          ]}
          rowActions={(p) => (
            <>
              <button
                type="button"
                className="xa-btn"
                disabled={busyKey === p.provider_key || !p.adapter_available}
                onClick={(e) => { e.stopPropagation(); onTest(p); }}
              >
                {busyKey === p.provider_key ? '시험 중…' : '연결 시험'}
              </button>
              <button
                type="button"
                className="xa-btn"
                disabled={!serverReady}
                onClick={(e) => { e.stopPropagation(); onOpen(p); }}
              >
                고치기
              </button>
            </>
          )}
        />
      )}
    </div>
  );
}
